<template>
    <div class="flex flex-col h-full">
        <div class="flex justify-between grow-0 px-2 py-2 bg-gray-100 dark:bg-neutral-950 media-picker__detail">
            <div></div>
            <div>
                <Button color="default" class="py-2" @click="close">
                    <icon icon="hero/outline/x" class="size-3"></icon>
                </Button>
            </div>
        </div>
        <div v-if="item" class="flex-auto">
            <header>
                <MediaPreview v-if="!isEdit" :media="item" class="max-h-48 min-h-48" />
                <div v-else class="overflow-y-auto overflow-x-hidden max-h-[40vh]">
                    <Field
                        v-if="item && item.is_image && item.url"
                        v-model="item.url"
                        label-position="none"
                        :field="{ adminComponent: 'AdminCropper', file: item }"
                        @cropped="cropped"
                    />
                </div>
            </header>
            <main class="px-6 py-2">
                <Field
                    v-model="form.name"
                    size="sm"
                    label-position="top"
                    :field="{
                        type: 'text',
                        adminComponent: 'AdminText',
                        name: lang('media::media.fields.name'),
                        readonly: false,
                    }"
                ></Field>

                <Field
                    v-model="form.alt"
                    size="sm"
                    label-position="top"
                    :field="{
                        type: 'text',
                        adminComponent: 'AdminText',
                        name: lang('media::media.fields.alt'),
                        readonly: false,
                    }"
                ></Field>

                <div class="grid grid-cols-12 py-2 text-xs opacity-50">
                    <div class="col-span-6">
                        {{ lang('media::media.fields.mime') }}:
                        {{ item.mime }}
                    </div>
                    <div class="col-span-6 text-right">
                        {{ lang('media::media.fields.size') }}:
                        {{ filesizeHumanReadable(item.size) }}
                    </div>
                </div>
            </main>
        </div>
        <div class="flex justify-between grow-0 px-2 py-2 bg-gray-100 dark:bg-neutral-950 media-picker__detail">
            <div class="space-x-2">
                <Button :href="route('media.admin.media.download', { id: item.id })" :a="true">
                    {{ lang('general.download') }}
                </Button>
                <Button v-if="item.is_image" color="default" @click="edit">
                    {{ lang('media::media.actions.edit_image') }}
                </Button>
            </div>
            <div>
                <Button color="default" @click="save">
                    {{ lang('general.save') }}
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { filesizeHumanReadable } from '@src/Common/Helpers/files';
import { useForm } from '@inertiajs/vue3';
import { useToast } from '@src/Common/Composables/useToast';
import MediaPreview from './MediaPreview.vue';

interface Props {
    item?: any;
}

const props = withDefaults(defineProps<Props>(), {
    item: {},
});

const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'updated', item: any): void;
}>();

const item = ref(props.item);

const close = () => {
    emit('close');
};

const form = useForm({
    id: props.item?.id,
    name: props.item?.name,
    file: null,
    alt: props.item?.alt,
});

const load = () => {
    axios.get(route('media.admin.api.media.info', { id: form.id })).then(({ data }) => {
        item.value = data;
    });
};

const save = () => {
    const formData = new FormData();

    formData.append('name', form.name);
    formData.append('alt', form.alt ?? '');

    if (form.file) {
        formData.append('file', form.file);
    }

    axios
        .post(route('media.admin.api.update', { id: form.id, _method: 'PUT' }), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then(({ data }) => {
            emit('close');
            emit('updated', data.object);

            load();

            if (data.message) {
                useToast({ message: data.message });
            }
        })
        .catch((error) => {
            useToast({ message: error.message, type: 'error' });
        });
};

/**
 * Edit image
 */

const isEdit = ref(false);

const edit = () => {
    isEdit.value = true;
};

const cropped = (data: File) => {
    form.file = data;

    item.value.url = URL.createObjectURL(data);

    isEdit.value = false;
};

watch(
    () => props.item,
    (value) => {
        item.value = value;

        form.id = value.id;
        form.name = value.name;
        form.alt = value.alt;
    },
);
</script>
